import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Camera, Square, Download, RotateCcw, Music, Upload,
  Loader2, X, Mic, MicOff, ArrowLeft, Volume2, VolumeX, Share2,
  Scissors, Link, ChevronDown, ChevronUp, Film
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_DURATION = 60;
const FRAME_URL = `${BACKEND_URL}/api/static/frames/afada_frame_portrait.png`;
const REC_W = 720;
const REC_H = 960;

/* ─── Download / Share helpers ─── */
const downloadFromUrl = (url, name) => {
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
};

const shareVideo = async (videoUrl) => {
  const text = 'Join us at the AFADA National Convention Boston 2026! July 31 - August 2, 2026. Cultural Heritage, Unity, Peace and Development!';
  const fileName = 'AFADA_Boston_2026_Invitation.mp4';
  try {
    if (navigator.share && navigator.canShare) {
      const resp = await fetch(videoUrl);
      const blob = await resp.blob();
      const file = new File([blob], fileName, { type: 'video/mp4' });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: 'AFADA National Convention Boston 2026', text });
        return;
      }
    }
  } catch (e) { if (e.name === 'AbortError') return; }
  downloadFromUrl(videoUrl, fileName);
  try { if (navigator.share) await navigator.share({ title: 'AFADA National Convention Boston 2026', text }); } catch { /* */ }
};

/* ─── Result buttons ─── */
const ResultActions = ({ processing, videoUrl, processingError, onRetry, onReset, resetLabel }) => (
  <div className="space-y-3">
    {processing && (
      <div className="text-center py-4">
        <Loader2 className="w-8 h-8 text-yellow-500 animate-spin mx-auto mb-2" />
        <p className="text-white/60 text-xs">Applying AFADA frame & intro...</p>
      </div>
    )}
    {processingError && (
      <div className="text-center py-3 space-y-2">
        <p className="text-red-400 text-xs">Processing failed</p>
        <Button onClick={onRetry} size="sm" className="bg-yellow-600 hover:bg-yellow-700 text-white">Retry</Button>
      </div>
    )}
    <div className="flex gap-2">
      <Button variant="outline" size="sm" onClick={onReset} className="border-white/20 text-white hover:bg-white/10 h-10 flex-1">
        <RotateCcw className="w-4 h-4 mr-1" /> {resetLabel}
      </Button>
      <Button onClick={() => videoUrl && downloadFromUrl(videoUrl, 'AFADA_Boston_2026.mp4')} disabled={!videoUrl || processing} className="bg-yellow-600 hover:bg-yellow-700 h-10 flex-1 disabled:opacity-40" data-testid="download-afada-btn">
        {processing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
        {processing ? 'Processing...' : 'Download MP4'}
      </Button>
    </div>
    <Button onClick={() => videoUrl && shareVideo(videoUrl)} disabled={!videoUrl || processing} className="bg-blue-600 hover:bg-blue-700 h-10 w-full disabled:opacity-40" data-testid="share-afada-btn">
      {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Share2 className="w-4 h-4 mr-2" />}
      {processing ? 'Processing...' : 'Share to WhatsApp & More'}
    </Button>
  </div>
);

const AfadaVideoPage = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('record');

  /* Record state */
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const animFrameRef = useRef(null);
  const audioContextRef = useRef(null);
  const musicNodeRef = useRef(null);
  const musicGainRef = useRef(null);
  const recordingFormat = useRef(null);
  const streamRef = useRef(null);
  const recProcessingId = useRef(0);
  const facingModeRef = useRef('user');
  const musicAudioRef = useRef(null);

  const [phase, setPhase] = useState('setup');
  const [cameraReady, setCameraReady] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [micEnabled, setMicEnabled] = useState(true);
  const [facingMode, setFacingMode] = useState('user');
  const [recProcessing, setRecProcessing] = useState(false);
  const [recVideoUrl, setRecVideoUrl] = useState(null);
  const [recError, setRecError] = useState(false);

  const [showMusic, setShowMusic] = useState(false);
  const [musicAudioUrl, setMusicAudioUrl] = useState(null);
  const [musicTitle, setMusicTitle] = useState('');
  const [musicSource, setMusicSource] = useState('none');
  const [musicVolume, setMusicVolume] = useState(15);
  const [musicPlaying, setMusicPlaying] = useState(false);
  const [audioDuration, setAudioDuration] = useState(0);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(60);
  const [audioUrlInput, setAudioUrlInput] = useState('');

  /* Upload state */
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreviewUrl, setUploadPreviewUrl] = useState(null);
  const [uplProcessing, setUplProcessing] = useState(false);
  const [uplVideoUrl, setUplVideoUrl] = useState(null);
  const [uplError, setUplError] = useState(false);

  const frameImg = useRef(null);

  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = FRAME_URL;
    img.onload = () => { frameImg.current = img; };
  }, []);

  /* Camera init */
  const initCamera = useCallback(async () => {
    try {
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
      const fm = facingModeRef.current;
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: fm, width: { ideal: REC_W }, height: { ideal: REC_H } },
        audio: micEnabled,
      });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play().catch(() => {}); }
      setCameraReady(true);
      startCanvasLoop();
    } catch (e) { console.error('Camera error:', e); }
  }, [micEnabled]);

  useEffect(() => {
    if (mode === 'record' && phase === 'setup') initCamera();
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, [mode, phase, initCamera]);

  useEffect(() => { facingModeRef.current = facingMode; }, [facingMode]);

  const startCanvasLoop = () => {
    const draw = () => {
      const canvas = canvasRef.current;
      const video = videoRef.current;
      if (!canvas || !video || video.readyState < 2) { animFrameRef.current = requestAnimationFrame(draw); return; }
      canvas.width = REC_W; canvas.height = REC_H;
      const ctx = canvas.getContext('2d');
      const vw = video.videoWidth, vh = video.videoHeight;
      const scale = Math.max(REC_W / vw, REC_H / vh);
      const sw = REC_W / scale, sh = REC_H / scale;
      const sx = (vw - sw) / 2, sy = (vh - sh) / 2;
      ctx.drawImage(video, sx, sy, sw, sh, 0, 0, REC_W, REC_H);
      if (frameImg.current) ctx.drawImage(frameImg.current, 0, 0, REC_W, REC_H);
      animFrameRef.current = requestAnimationFrame(draw);
    };
    draw();
  };

  /* Recording */
  const startRecording = () => {
    setCountdown(3);
    const tick = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) { clearInterval(tick); beginCapture(); return 0; }
        return c - 1;
      });
    }, 1000);
  };

  const beginCapture = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    chunksRef.current = [];

    const canvasStream = canvas.captureStream(30);
    const audioTracks = streamRef.current?.getAudioTracks() || [];

    let dest = null;
    if (audioTracks.length > 0 || musicAudioUrl) {
      const ac = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = ac;
      dest = ac.createMediaStreamDestination();
      if (audioTracks.length > 0) {
        const micSrc = ac.createMediaStreamSource(new MediaStream(audioTracks));
        micSrc.connect(dest);
      }
      if (musicAudioUrl && musicAudioRef.current) {
        try {
          const node = ac.createMediaElementSource(musicAudioRef.current);
          const gain = ac.createGain();
          gain.gain.value = musicVolume / 100;
          node.connect(gain); gain.connect(dest); gain.connect(ac.destination);
          musicNodeRef.current = node; musicGainRef.current = gain;
          musicAudioRef.current.currentTime = trimStart;
          musicAudioRef.current.play();
        } catch { /* */ }
      }
    }

    const combined = new MediaStream([
      ...canvasStream.getVideoTracks(),
      ...(dest ? dest.stream.getAudioTracks() : audioTracks),
    ]);

    let mimeType = 'video/webm;codecs=vp8,opus';
    if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = 'video/webm';
    if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = 'video/mp4';
    recordingFormat.current = mimeType;

    const mr = new MediaRecorder(combined, { mimeType, videoBitsPerSecond: 2_500_000 });
    mr.ondataavailable = e => { if (e.data?.size > 0) chunksRef.current.push(e.data); };
    mr.onstop = () => finishRecording();
    mediaRecorderRef.current = mr;
    mr.start(500);
    setPhase('recording'); setElapsed(0);
  };

  useEffect(() => {
    if (phase !== 'recording') return;
    const iv = setInterval(() => {
      setElapsed(e => {
        if (e + 1 >= MAX_DURATION) { stopRecording(); return e; }
        if (musicAudioRef.current && musicAudioRef.current.currentTime >= trimEnd) {
          musicAudioRef.current.currentTime = trimStart;
        }
        return e + 1;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [phase]);

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') mediaRecorderRef.current.stop();
    if (musicAudioRef.current) { musicAudioRef.current.pause(); musicAudioRef.current.currentTime = 0; }
  };

  const finishRecording = () => {
    const blob = new Blob(chunksRef.current, { type: recordingFormat.current || 'video/webm' });
    setRecordedBlob(blob);
    setPreviewUrl(URL.createObjectURL(blob));
    setPhase('preview');
    processRecording(blob);
  };

  const processRecording = async (blob) => {
    const pid = ++recProcessingId.current;
    setRecProcessing(true); setRecError(false); setRecVideoUrl(null);
    try {
      const fd = new FormData();
      fd.append('main', blob, 'recording.webm');
      fd.append('mirror', facingModeRef.current === 'user' ? 'true' : 'false');
      const resp = await axios.post(`${API_URL}/afada/video/build-final`, fd, { responseType: 'blob', timeout: 180000 });
      if (pid !== recProcessingId.current) return;
      setRecVideoUrl(URL.createObjectURL(resp.data));
    } catch {
      if (pid === recProcessingId.current) setRecError(true);
    } finally { if (pid === recProcessingId.current) setRecProcessing(false); }
  };

  const retryRecording = () => { if (recordedBlob) processRecording(recordedBlob); };
  const resetRecording = () => {
    setPhase('setup'); setRecordedBlob(null); setPreviewUrl(null); setRecVideoUrl(null); setRecError(false);
  };

  /* Upload */
  const handleVideoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadFile(file); setUploadPreviewUrl(URL.createObjectURL(file));
    setUplVideoUrl(null); setUplError(false);
  };

  const startUploadProcessing = async () => {
    if (!uploadFile) return;
    setUplProcessing(true); setUplError(false); setUplVideoUrl(null);
    try {
      const fd = new FormData();
      fd.append('video', uploadFile);
      const resp = await axios.post(`${API_URL}/afada/video/upload-process`, fd, { responseType: 'blob', timeout: 300000 });
      setUplVideoUrl(URL.createObjectURL(resp.data));
    } catch { setUplError(true); }
    finally { setUplProcessing(false); }
  };
  const retryUpload = () => startUploadProcessing();
  const resetUpload = () => { setUploadFile(null); setUploadPreviewUrl(null); setUplVideoUrl(null); setUplError(false); };

  /* Music */
  const handleMusicUpload = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setMusicAudioUrl(URL.createObjectURL(file)); setMusicTitle(file.name); setMusicSource('upload');
  };
  const handleAudioUrl = () => {
    if (!audioUrlInput.trim()) return;
    setMusicAudioUrl(audioUrlInput.trim()); setMusicTitle(audioUrlInput.split('/').pop()); setMusicSource('url');
  };
  const clearMusic = () => {
    setMusicAudioUrl(null); setMusicTitle(''); setMusicSource('none');
    setAudioDuration(0); setTrimStart(0); setTrimEnd(60);
  };
  const onAudioLoaded = () => {
    if (musicAudioRef.current) { const d = Math.floor(musicAudioRef.current.duration); setAudioDuration(d); setTrimEnd(Math.min(d, 60)); }
  };
  const toggleMusicPreview = () => {
    if (!musicAudioRef.current) return;
    if (musicPlaying) { musicAudioRef.current.pause(); setMusicPlaying(false); }
    else { musicAudioRef.current.volume = musicVolume / 100; musicAudioRef.current.currentTime = trimStart; musicAudioRef.current.play(); setMusicPlaying(true); }
  };

  const fmtTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0a0a0a' }}>
      {/* AFADA-branded header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-yellow-900/30" style={{ background: 'linear-gradient(to right, #1a0a00, #0a0a0a, #1a0a00)' }}>
        <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-white/60 hover:text-white" data-testid="back-home">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="text-center">
          <h1 className="font-bold text-sm text-yellow-500" data-testid="afada-title">AFADA NORTH AMERICA</h1>
          <p className="text-[10px] text-white/50">National Convention Boston 2026</p>
        </div>
        <div className="w-16" />
      </div>

      <div className="max-w-md mx-auto px-4 py-4 space-y-4">
        {/* Event banner */}
        <div className="rounded-xl overflow-hidden" style={{ background: 'linear-gradient(135deg, #1a0a00, #2a1500, #0a0a0a)', border: '1px solid rgba(212,175,55,0.2)' }}>
          <div className="flex items-center gap-3 p-3">
            <img src={`${BACKEND_URL}/api/static/frames/afada_logo.jpg`} alt="AFADA" className="w-12 h-12 rounded-full object-cover border-2 border-yellow-600" />
            <div>
              <p className="text-yellow-500 font-bold text-sm">AFADA National Convention</p>
              <p className="text-white/40 text-[10px]">Boston &middot; July 31 - August 2, 2026</p>
              <p className="text-white/25 text-[9px] italic">Cultural Heritage, Unity, Peace and Development</p>
            </div>
          </div>
        </div>

        {/* Mode toggle */}
        <div className="flex rounded-lg overflow-hidden border border-yellow-900/30" data-testid="afada-mode-toggle">
          <button onClick={() => setMode('record')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-xs font-medium transition-colors ${mode === 'record' ? 'bg-red-700 text-white' : 'text-white/50 hover:text-white hover:bg-white/5'}`} data-testid="afada-mode-record">
            <Camera className="w-3.5 h-3.5" /> Record
          </button>
          <button onClick={() => setMode('upload')} className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-xs font-medium transition-colors ${mode === 'upload' ? 'bg-yellow-700 text-white' : 'text-white/50 hover:text-white hover:bg-white/5'}`} data-testid="afada-mode-upload">
            <Upload className="w-3.5 h-3.5" /> Upload Video
          </button>
        </div>

        {/* ══════════ RECORD MODE ══════════ */}
        {mode === 'record' && (
          <>
            <div className="relative bg-black rounded-xl overflow-hidden mx-auto" style={{ width: '100%', aspectRatio: `${REC_W}/${REC_H}`, border: '2px solid rgba(212,175,55,0.15)' }} data-testid="afada-video-viewport">
              <video ref={videoRef} className="hidden" muted playsInline />
              {phase !== 'preview' && <canvas ref={canvasRef} className="w-full h-full block" data-testid="afada-canvas" />}
              {phase === 'preview' && previewUrl && <video src={previewUrl} controls autoPlay loop playsInline className="w-full h-full object-contain bg-black" data-testid="afada-preview" />}
              {phase === 'countdown' && <div className="absolute inset-0 flex items-center justify-center bg-black/60 z-10"><span className="text-7xl font-bold text-yellow-500 drop-shadow-lg animate-pulse">{countdown}</span></div>}
              {phase === 'recording' && <div className="absolute top-3 left-3 flex items-center gap-2 px-2.5 py-1 rounded-full bg-red-600/90 z-10 shadow-lg"><div className="w-2 h-2 rounded-full bg-white animate-pulse" /><span className="text-white text-xs font-mono font-bold">{fmtTime(elapsed)} / {fmtTime(MAX_DURATION)}</span></div>}
              {!cameraReady && phase !== 'preview' && <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black"><Loader2 className="w-7 h-7 text-yellow-500 animate-spin" /><p className="text-white/50 text-xs">Starting camera...</p></div>}
            </div>

            <div className="space-y-3">
              {phase === 'setup' && (
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setMicEnabled(m => !m)} className="border-white/20 text-white hover:bg-white/10 h-9 w-9 p-0" data-testid="afada-mic-toggle">{micEnabled ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4 text-red-400" />}</Button>
                  <Button variant="outline" size="sm" onClick={() => setFacingMode(f => f === 'user' ? 'environment' : 'user')} className="border-white/20 text-white hover:bg-white/10 h-9 w-9 p-0" data-testid="afada-flip"><RotateCcw className="w-4 h-4" /></Button>
                  <Button onClick={startRecording} disabled={!cameraReady} className="bg-red-700 hover:bg-red-800 h-10 px-5 text-sm" data-testid="afada-start-recording"><Camera className="w-4 h-4 mr-2" /> Start Recording</Button>
                </div>
              )}
              {phase === 'recording' && <div className="flex justify-center"><Button onClick={stopRecording} className="bg-red-600 hover:bg-red-700 h-12 px-6 text-base touch-manipulation" data-testid="afada-stop-recording"><Square className="w-5 h-5 mr-2" /> Stop Recording ({MAX_DURATION - elapsed}s)</Button></div>}
              {phase === 'preview' && <ResultActions processing={recProcessing} videoUrl={recVideoUrl} processingError={recError} onRetry={retryRecording} onReset={resetRecording} resetLabel="Re-take" />}
            </div>

            {/* Music panel */}
            {phase === 'setup' && (
              <div className="border border-yellow-900/30 rounded-lg overflow-hidden">
                <button onClick={() => setShowMusic(!showMusic)} className="w-full flex items-center justify-between p-3 text-white/70 hover:text-white hover:bg-white/5 transition-colors" data-testid="afada-music-toggle">
                  <div className="flex items-center gap-2"><Music className="w-4 h-4 text-yellow-500" /><span className="text-xs font-medium">{musicAudioUrl ? `\u266A ${musicTitle}` : 'Add Background Music'}</span></div>
                  {showMusic ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {showMusic && (
                  <div className="p-3 pt-0 space-y-2.5 border-t border-yellow-900/20">
                    {musicAudioUrl ? (
                      <div className="space-y-2.5">
                        <div className="p-2 rounded-lg bg-yellow-900/10 border border-yellow-700/20"><p className="text-[9px] text-white/50">{musicSource === 'url' ? 'From URL' : 'Uploaded'}</p><p className="text-[11px] text-white font-medium truncate">{musicTitle}</p></div>
                        <div className="space-y-1"><Label className="text-white/60 text-[10px] flex items-center gap-1"><Volume2 className="w-3 h-3" /> Volume: {musicVolume}%</Label><input type="range" min="5" max="50" value={musicVolume} onChange={(e) => { const v = parseInt(e.target.value); setMusicVolume(v); if (musicAudioRef.current) musicAudioRef.current.volume = v / 100; if (musicGainRef.current) musicGainRef.current.gain.value = v / 100; }} className="w-full h-1.5 accent-yellow-500" /></div>
                        {audioDuration > 0 && <div className="space-y-1"><Label className="text-white/60 text-[10px] flex items-center gap-1"><Scissors className="w-3 h-3" /> Trim: {fmtTime(trimStart)} — {fmtTime(trimEnd)}</Label><div className="flex gap-2 items-center"><div className="flex-1"><input type="range" min="0" max={Math.max(audioDuration - 5, 0)} value={trimStart} onChange={(e) => { const v = parseInt(e.target.value); setTrimStart(v); if (v >= trimEnd) setTrimEnd(Math.min(v + 10, audioDuration)); }} className="w-full h-1 accent-yellow-600" /></div><div className="flex-1"><input type="range" min={trimStart + 5} max={audioDuration} value={trimEnd} onChange={(e) => setTrimEnd(parseInt(e.target.value))} className="w-full h-1 accent-red-600" /></div></div></div>}
                        <div className="flex gap-1.5"><Button size="sm" variant="outline" onClick={toggleMusicPreview} className="flex-1 h-7 border-white/20 text-white hover:bg-white/10 text-[10px]">{musicPlaying ? <><VolumeX className="w-3 h-3 mr-1" />Pause</> : <><Volume2 className="w-3 h-3 mr-1" />Preview</>}</Button><Button size="sm" variant="ghost" onClick={clearMusic} className="h-7 text-red-400 hover:bg-red-400/10 px-2"><X className="w-3 h-3" /></Button></div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <label className="flex items-center gap-2 p-2 rounded-lg border border-dashed border-white/20 hover:border-white/40 cursor-pointer"><Upload className="w-3.5 h-3.5 text-white/50" /><span className="text-[10px] text-white/50">Upload MP3 / WAV...</span><input type="file" accept="audio/*" onChange={handleMusicUpload} className="hidden" /></label>
                        <div className="flex items-center gap-2"><div className="flex-1 h-px bg-white/10" /><span className="text-[8px] text-white/30">OR</span><div className="flex-1 h-px bg-white/10" /></div>
                        <div><Label className="text-white/60 text-[10px] flex items-center gap-1"><Link className="w-3 h-3" /> Audio URL</Label><div className="flex gap-1 mt-0.5"><Input placeholder="https://...mp3" value={audioUrlInput} onChange={(e) => setAudioUrlInput(e.target.value)} className="bg-white/5 border-white/10 text-white text-[10px] h-7" /><Button size="sm" onClick={handleAudioUrl} disabled={!audioUrlInput.trim()} className="bg-yellow-700 hover:bg-yellow-800 h-7 px-2 text-[10px]">Load</Button></div></div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {musicAudioUrl && <audio ref={musicAudioRef} src={musicAudioUrl} preload="auto" crossOrigin="anonymous" onLoadedMetadata={onAudioLoaded} className="hidden" />}
            {phase === 'setup' && <p className="text-center text-white/30 text-[10px]">Max {MAX_DURATION}s &middot; AFADA frame & intro added automatically</p>}
          </>
        )}

        {/* ══════════ UPLOAD MODE ══════════ */}
        {mode === 'upload' && (
          <>
            {!uploadFile ? (
              <div className="space-y-4">
                <label className="flex flex-col items-center justify-center gap-3 p-8 rounded-xl border-2 border-dashed border-yellow-900/30 hover:border-yellow-600/50 cursor-pointer transition-colors bg-white/[0.02]" data-testid="afada-upload-dropzone">
                  <Film className="w-10 h-10 text-yellow-600/60" />
                  <div className="text-center">
                    <p className="text-white/70 text-sm font-medium">Tap to select a video</p>
                    <p className="text-white/30 text-[10px] mt-1">MP4, MOV, WebM &middot; Max 100MB</p>
                  </div>
                  <input type="file" accept="video/*" onChange={handleVideoUpload} className="hidden" data-testid="afada-upload-input" />
                </label>
                <p className="text-center text-white/30 text-[10px]">Your video will be framed with the AFADA National Convention Boston 2026 branding. The full video will be visible — nothing gets hidden by the frame borders.</p>
              </div>
            ) : (
              <>
                <div className="relative bg-black rounded-xl overflow-hidden mx-auto" style={{ width: '100%', aspectRatio: `${REC_W}/${REC_H}`, border: '2px solid rgba(212,175,55,0.15)' }} data-testid="afada-upload-preview">
                  <video src={uploadPreviewUrl} controls playsInline className="w-full h-full object-contain bg-black" />
                </div>
                <div className="space-y-3">
                  {!uplVideoUrl && !uplProcessing && !uplError && (
                    <Button onClick={startUploadProcessing} className="bg-yellow-600 hover:bg-yellow-700 text-black font-semibold h-10 w-full" data-testid="afada-apply-frame-btn">
                      <Film className="w-4 h-4 mr-2" /> Apply AFADA Frame & Intro
                    </Button>
                  )}
                  <ResultActions processing={uplProcessing} videoUrl={uplVideoUrl} processingError={uplError} onRetry={retryUpload} onReset={resetUpload} resetLabel="Choose Another" />
                </div>
              </>
            )}
          </>
        )}

        {/* Footer branding */}
        <div className="text-center py-4 border-t border-yellow-900/20">
          <p className="text-yellow-600/60 text-[10px] font-bold">AFADA &middot; Aghem Family and Development Association</p>
          <p className="text-white/20 text-[9px]">Cultural Heritage, Unity, Peace and Development</p>
        </div>
      </div>
    </div>
  );
};

export default AfadaVideoPage;
